import { useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Ticket } from 'lucide-react';
import type { Client, ClientCreateBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { pageCountOf, Pagination } from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import {
  CLIENTS_PAGE_SIZE,
  clientsListOptions,
  errorMessage,
} from '@/features/clients/clients-queries';

export const clientsSearchSchema = z.object({
  q: z.string().default(''),
  page: z.coerce.number().int().min(1).default(1),
});

const routeApi = getRouteApi('/_authenticated/clients');

// ── Chip de status (ponto verde + rótulo, estilo Tiflux) ────────────────────
export function ClientStatusDot({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px]">
      <span
        className={cn('size-2 shrink-0 rounded-full', active ? 'bg-success' : 'bg-border')}
        aria-hidden
      />
      <span className={active ? undefined : 'text-muted-foreground'}>
        {active ? 'Ativo' : 'Inativo'}
      </span>
    </span>
  );
}

const COLS = ['Nome', 'Razão social', 'CNPJ/CPF', 'Status', 'Tickets'] as const;

export function ClientsPage() {
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const [term, setTerm] = useState(search.q);
  const [createOpen, setCreateOpen] = useState(false);
  const list = useQuery(clientsListOptions({ q: search.q, page: search.page }));

  useEffect(() => {
    const t = setTimeout(() => {
      if (term !== search.q) {
        void navigate({ to: '/clients', search: { q: term, page: 1 }, replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [term, search.q, navigate]);

  const total = list.data?.total ?? 0;
  const pageCount = pageCountOf(total, CLIENTS_PAGE_SIZE);

  const openClient = (client: Client) =>
    void navigate({ to: '/clients/$clientId', params: { clientId: String(client.id) } });

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Cabeçalho: título + contagem + busca + CTA */}
      <div className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl leading-7 font-semibold">Clientes</h1>
        {list.data ? (
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
            {total}
          </span>
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar"
              aria-label="Buscar clientes"
              className="h-8 w-56 pl-8"
            />
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" strokeWidth={2.5} />
            Novo cliente
          </Button>
        </div>
      </div>

      {/* Tabela */}
      {list.isPending ? (
        <div aria-busy className="border-t">
          <div className="flex items-center gap-6 bg-muted px-4 py-3">
            {COLS.map((c) => (
              <Skeleton key={c} className="h-3 w-24" />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-6 border-b border-divider px-4 py-3 last:border-0">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      ) : list.isError ? (
        <EmptyState
          className="border-t"
          message={`Erro ao carregar clientes: ${errorMessage(list.error)}`}
        />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          className="h-52 border-t"
          message={search.q ? 'Nenhum cliente encontrado para a busca' : 'Nenhum cliente cadastrado'}
        />
      ) : (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px] text-foreground">
                <th className="h-10 px-4 font-semibold">Nome</th>
                <th className="h-10 px-4 font-semibold">Razão social</th>
                <th className="h-10 px-4 font-semibold">CNPJ/CPF</th>
                <th className="h-10 px-4 font-semibold">Status</th>
                <th className="h-10 w-20 px-4 text-right font-semibold">Tickets</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => openClient(c)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') openClient(c);
                  }}
                  className="h-10 cursor-pointer border-b border-divider transition-colors last:border-0 hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
                >
                  <td className="max-w-96 truncate px-4 font-medium">{c.name}</td>
                  <td className="max-w-72 truncate px-4 text-muted-foreground">
                    {c.social ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                    {c.socialRevenue ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    <ClientStatusDot active={c.status} />
                  </td>
                  <td className="px-4 text-right">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: String(c.id) }}
                      onClick={(e) => e.stopPropagation()}
                      title="Ver tickets do cliente"
                      aria-label={`Ver tickets de ${c.name}`}
                      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
                    >
                      <Ticket className="size-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rodapé: contagem + paginação */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
        <span className="text-[13px] text-muted-foreground">
          {total} cliente{total === 1 ? '' : 's'} — página {search.page} de {pageCount}
        </span>
        <Pagination
          page={search.page}
          pageCount={pageCount}
          onPage={(page) => void navigate({ to: '/clients', search: { q: search.q, page } })}
        />
      </div>

      {createOpen ? <NewClientDialog onClose={() => setCreateOpen(false)} /> : null}
    </div>
  );
}

function NewClientDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [social, setSocial] = useState('');
  const [socialRevenue, setSocialRevenue] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: async (body: ClientCreateBody) => {
      const res = await api<Client>('/clients', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] });
      onClose();
      void navigate({ to: '/clients/$clientId', params: { clientId: String(client.id) } });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({
      name: name.trim(),
      social: social.trim() || null,
      socialRevenue: socialRevenue.trim() || null,
      status: true,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open onClose={onClose} title="Novo cliente">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="client-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="client-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-social">Razão social</Label>
          <Input id="client-social" value={social} onChange={(e) => setSocial(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-doc">CNPJ/CPF</Label>
          <Input
            id="client-doc"
            value={socialRevenue}
            onChange={(e) => setSocialRevenue(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-notes">Anotações</Label>
          <Textarea id="client-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Criando…' : 'Criar cliente'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
